import { useState, useCallback, useRef } from "react";
import { useAPIMutation } from "@/services/api/hook";

export interface EventHookConfig {
  hook_id: string;
  loading_text?: string;
  button_label?: string;
}

export interface EventHookPayload {
  entityId: string;
}

export interface UseEventHookOptions {
  onSuccess?: (data: unknown) => void;
  onError?: (error: unknown) => void;
}

export const useEventHook = (
  config: EventHookConfig,
  options?: UseEventHookOptions
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutation = useAPIMutation<
    { data: EventHookPayload; pathParams: { hookId: string } },
    unknown
  >(
    "event_hooks/execute",
    { method: "POST" },
    (data) => {
      setIsLoading(false);
      setError(null);
      options?.onSuccess?.(data);
    },
    (error) => {
      setIsLoading(false);
      setError(error instanceof Error ? error : new Error(String(error)));
      options?.onError?.(error);
    }
  );

  const mutationRef = useRef(mutation);
  mutationRef.current = mutation;

  const execute = useCallback(
    (entityId: string) => {
      setIsLoading(true);
      setError(null);
      mutationRef.current.mutate({
        data: { entityId },
        pathParams: { hookId: config.hook_id }
      });
    },
    [config.hook_id]
  );

  return {
    execute,
    isLoading: isLoading || mutation.isPending,
    error,
    loadingText: config.loading_text || "Loading..."
  };
};

export default useEventHook;
